import styles from "./BrandWatermark.module.css";

export type BrandWatermarkCorner = "bottom-right" | "bottom-left" | "top-right" | "top-left";

export type BrandWatermarkProps = {
  /** Coin d'ancrage. `bottom-right` par défaut : c'est là qu'il gêne le moins la lecture. */
  corner?: BrandWatermarkCorner;
  /** Largeur du symbole, en % du conteneur ou en px. */
  width?: string;
  /** Opacité. 0,06 sur l'encre, mesuré ; au-delà de 0,10 il cesse d'être un filigrane. */
  opacity?: number;
  /** Symbole encre au lieu du symbole blanc, pour un fond clair ou jaune. */
  ink?: boolean;
  /** Rotation, en degrés. */
  angle?: number;
  className?: string;
};

/**
 * LE FILIGRANE DE MARQUE : le symbole `[Y]` agrandi, tourné, débordant d'un
 * coin, à très faible opacité. Sa raison d'être est de casser un aplat sombre
 * et de lui donner un peu de profondeur (demande de Pierre du 24/09/2026,
 * validée au rendu sur `ComparisonSpotlight`).
 *
 * Ce n'est PAS une trame géométrique : relevé le même jour sur agenceakiro.fr,
 * ce qu'ils posent sur leurs fonds sombres est leur propre logo, agrandi à plus
 * de 1800 px, tourné de 9 degrés, débordant en haut à droite, à 3 % d'opacité.
 * On avait déjà ce geste dans la maison, la prop `watermark` de
 * `FloatingStatement` du 19/09 : ce composant le rend disponible partout, et le
 * logo reste le seul ornement du fond.
 *
 * Trois choses apprises au rendu et figées ici :
 * · c'est le SYMBOLE seul (`mono-white.svg`, 188x142), jamais le verrouillage
 *   `logo-white.png` (508x93), qui en `contain` se réduit à un trait ;
 * · les 3 % d'Akiro sont calés sur un bleu nuit ; sur notre encre quasi noire
 *   il faut environ 6 % ;
 * · il s'ancre LOIN du texte. En haut il passe derrière les titres et gêne la
 *   lecture ; en bas il occupe la zone que le contenu laisse vide.
 *
 * Le parent doit être en `position: relative` et en `isolation: isolate` : le
 * filigrane est en `z-index: -1` pour rester derrière le contenu sans forcer le
 * contenu à se positionner.
 */
export function BrandWatermark({
  corner = "bottom-right",
  width = "58%",
  opacity = 0.06,
  ink = false,
  angle = -9,
  className,
}: BrandWatermarkProps) {
  return (
    <span
      aria-hidden="true"
      className={[styles.watermark, className].filter(Boolean).join(" ")}
      data-corner={corner}
      style={{
        width,
        opacity,
        transform: `rotate(${angle}deg)`,
        backgroundImage: `url(/yellow/${ink ? "mono-ink" : "mono-white"}.svg)`,
      }}
    />
  );
}
